import classNames from 'classnames';
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { noop, bind, compose, compact, isFunction, get, isUndefined } from 'underscore';
import { FilterViewManagerWithGlobalFilterContext } from '@bingads-webui-campaign-react/filter-view-manager';
import { StyledGridViewWrapper } from '@bingads-webui-campaign-react/grid-view-wrapper';
import { getToolbarEditor } from '@bingads-webui-campaign-react/toolbar-plugin';
import { TopGridPanel } from '@bingads-webui-campaign-react/top-grid-panel';
import { withGlobalFilter } from '@bingads-webui-campaign-react/global-filter-context';
import { StyledTablePageContainer } from '@bingads-webui-campaign-react/table-page-container';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withTablePageStateManager } from '@bingads-webui-campaign-react/table-page-state-manager';
import { withSiteMap } from '@bingads-webui-campaign-react/with-site-map';
import { DisclaimerBannerWithInstore } from '@bingads-webui-campaign-react/help';
import { ProgressiveLoadingBlock, getFilteredCampaignTypeFromRawFilter, CampaignType } from '@bingads-webui-campaign-react/shell-ui-common';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { withNoteStateManager, NotesPanel, NoteDialog } from '@bingads-webui-campaign-react/note-editor';
import { dismissPopupIfExist } from '@bingads-webui-campaign/fluctuation-common';
import { RECOMMENDATION_TYPES } from '@bingads-webui-campaign/recommendation-core';
import { withHotKeys, ShortcutType } from '@bingads-webui-react/hot-keys';
import { seasonalityPredicateValueNameGetter } from '@bingads-webui-campaign/seasonality-id-name-utils';
import jsdata from '@bingads-webui-component-legacy/js-data-campaign/index';
import { loadInlineChartWithWebpack } from '@bingads-webui-campaign-react/inline-chart-factory-webpack';
import { withCampaignUpgradeManager } from '@bingads-webui-campaign-react/campaign-upgrade-tool';
import { withDelayedStyles } from '@bingads-webui-campaign-react/common-utils';
import eventDispatcher from '@bingads-webui-component-legacy/event-dispatcher/index';
import { MessageBar, Link } from '@fluentui/react';
import { WaterfallFilterType } from '@bingads-webui-campaign/filter-storage';
import { ShoppingFACScenario } from '@bingads-webui-campaign-react/shopping-coupon-callout';
import { ReactTemplate } from '@bingads-webui-react/react-template';

import { createCampaignsGridForUiNext } from './campaigns-factory';
import { getToolbarConfig } from './toolbar-config';
import { onBeforeApplySavedFilter } from './campaign-filter-utilities';

const AutoBiddingCouponCallout = React.lazy(() => import(/* webpackMode: 'eager' */'@bingads-webui-campaign-react/autobidding-coupon-callout'));
const ShoppingCouponCallout = React.lazy(() => import(/* webpackMode: 'eager' */'@bingads-webui-campaign-react/shopping-coupon-callout'));
const StyledCampaignUpgradeDialog = React.lazy(() => import(/* webpackMode: 'eager' */'@bingads-webui-campaign-react/campaign-upgrade-tool'));

const getDelayedCampaignStyles = () => (
  import(/* webpackMode: 'eager' */'./campaigns-page-styles')
    .then(({ getCampaignsGridViewStyles }) => ({
      getDelayedStyles: getCampaignsGridViewStyles,
    }))
);

const getDefaultStyles = () => ({
  inlineChartContainer: {
    '& .flex-chart-panel': {
      display: 'none',
    },
  },
});

const messageBarStyle = {
  root: {
    background: '#eff6fc',
    '& span.iconba-Insights': {
      fontSize: '16px',
      top: 0,
      color: '#0078D4',
    },
  },
};

const StyledGridViewWrapperWithFilterContext = withGlobalFilter(StyledGridViewWrapper);

class Campaigns extends React.PureComponent {
  constructor(props) {
    super(props);
    this.columnAction = noop;
    this.chartView = null;
    this.viewport = React.createRef();
    this.createGrid(props);
  }

  componentDidMount() {
    const pageContainer = document.querySelector('.table-page-container');

    if (pageContainer) {
      pageContainer.addEventListener('scroll', () => dismissPopupIfExist([document.getElementById('mcc-perf-fluctuation-placeholder')]));
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.hotKey === 'shift+n') {
      this.props.scope.save('/campaign/vnext/createcampaign/objective');
    }
    if (prevProps.scope !== this.props.scope) {
      this.createGrid(this.props);
    }

    if (this.isColumnSetChangedOnUpdate(prevProps)) {
      this.createGrid(this.props);

      if (this.state && this.state.backboneGridView && this.state.backboneGridView.grid && this.state.backboneGridView.grid.columnChooser) {
        this.state.backboneGridView.grid.columnChooser.restoreToolbar();
      }
    }
  }

  componentWillUnmount() {
    if (this.state.backboneGridView) {
      this.state.backboneGridView.remove();
    }
    this.willUnMount = true;
  }

  getSscUpgradeMessage(showPhase12Message) {
    const { preferenceService } = this.props;

    const onDismissSscUpgradeBanner = () => {
      if (!isUndefined(preferenceService)) {
        const preferenceName = showPhase12Message ? 'Phase12SscUpgradedMessageDismissed' : 'Phase0SscUpgradedMessageDismissed';
        const stateKey = showPhase12Message ? 'phase12SscUpgradedMessageDismissed' : 'phase0SscUpgradedMessageDismissed';

        preferenceService.setAtAccountLevel({
          Name: preferenceName,
          Data: true,
        });

        this.setState({
          [stateKey]: true,
          isSscUpgradedMessageDismissed: true,
        });
      }
    };

    const messageContent = showPhase12Message ?
      _TL_('We\'ve upgraded your Smart Shopping campaigns to Performance Max campaigns, which use the best of Microsoft\'s automation to find you more conversions. {{learnMore}}{{/learnMore}}') :
      _TL_('We\'ve upgraded some of your Smart Shopping campaigns to Performance Max campaigns, which use the best of Microsoft\'s automation to find you more conversions. {{learnMore}}{{/learnMore}}');

    const PmaxLearnMore = () => (
      <Link target="_blank" href="https://about.ads.microsoft.com/en-us/blog/post/march-2024/microsofts-performance-max-now-available-globally">
        {this.props.i18n.getString(_TL_('Learn more about Performance Max campaigns.'))}
      </Link>
    );

    return (
      <MessageBar
        className="message-bar"
        isMultiline
        messageBarIconProps={{ iconName: 'Info' }}
        styles={messageBarStyle}
        onDismiss={() => onDismissSscUpgradeBanner()}
      >
        <ReactTemplate
          template={this.props.i18n.getString(messageContent)}
          model={{ learnMore: PmaxLearnMore }}
        />
      </MessageBar>
    );
  }

  isColumnSetChangedOnUpdate(prevProps) {
    // Checking if global filter changed in a way that would require grid, column chooser or performance chart to update.
    const campaignType = getFilteredCampaignTypeFromRawFilter(this.props[WaterfallFilterType.CampaignType]);
    const prevCampaignType = getFilteredCampaignTypeFromRawFilter(prevProps[WaterfallFilterType.CampaignType]);

    // no global filter change
    if (campaignType === prevCampaignType) {
      return false;
    }

    // return true if we entered or left App or All global filter
    return campaignType === CampaignType.App || campaignType === CampaignType.All || prevCampaignType === CampaignType.App || prevCampaignType === CampaignType.All;
  }

  createGrid(props) {
    const {
      serverContext,
      scope,
      preferenceService,
      permissions,
      crossPageSelectPlugin,
      toolbarPlugin,
      onGridInjected,
      toolbarSelectedConfig,
      showTopOfGridPanel,
      toolbarUnselectedConfig,
      updateToolbar,
      siteMap,
      i18n: newI18n,
      resetToolbarVisibility,
      perfMarker,
      pageName,
      deps,
      preexecuteFlag,
      theme,
      campaignUpgradeProps,
      prefetchChunks,
    } = props;

    this.contexts = {
      serverContext,
      scope,
      preferenceService,
      siteMap,
      permissions,
    };

    this.columnAction = noop;
    this.chartView = null;
    this.viewport = React.createRef();
    this.scope = scope;
    this.willUnMount = false;

    if (perfMarker) {
      perfMarker.doneCallback = () => {
        eventDispatcher.trigger('campaigns_grid_loaded');
        if (isFunction(prefetchChunks)) {
          prefetchChunks();
        }
      };
    }

    const phase0SscUpgradedMessageDismissed = preferenceService.findByNameAtAccountLevel('Phase0SscUpgradedMessageDismissed');
    const phase12SscUpgradedMessageDismissed = preferenceService.findByNameAtAccountLevel('Phase12SscUpgradedMessageDismissed');

    if (this.state && this.state.backboneGridView) {
      this.state.backboneGridView.remove();
    }

    if (this.state) {
      this.setState({
        backboneGridView: null,
        phase0SscUpgradedMessageDismissed: !!phase0SscUpgradedMessageDismissed,
        phase12SscUpgradedMessageDismissed: !!phase12SscUpgradedMessageDismissed,
      });
    } else {
      this.state = {
        backboneGridView: null,
        phase0SscUpgradedMessageDismissed: !!phase0SscUpgradedMessageDismissed, // flag from Preference which marks if the user dismissed the message in previous visits
        phase12SscUpgradedMessageDismissed: !!phase12SscUpgradedMessageDismissed,
        isSscUpgradedMessageDismissed: false, // flag to mark if the message is dismissed in current visit
      };
    }
    const { onSelectCrossPage } = crossPageSelectPlugin;
    const campaignsGridPromise = createCampaignsGridForUiNext({
      serverContext,
      scope,
      preferenceService,
      permissions,
      onSelectCrossPage,
      viewport: this.viewport.current,
      crossPageSelectPlugin,
      toolbarPlugin,
      newI18n: props.i18n,
      siteMap,
      perfMarker,
      pageName,
      willParentViewUnMount: () => this.willUnMount,
      deps,
      preexecuteFlag,
      theme,
      campaignUpgradeProps,
    });

    const CampaignsGridView = React.lazy(() => { // eslint-disable-line arrow-body-style
      return campaignsGridPromise.then(({
        gridView, gridDataSource, filterService, i18n,
      }) => {
        if (this.willUnMount) {
          return {};
        }
        onGridInjected({
          backboneGridView: gridView,
          deselectAll: gridView.grid.gridView.deselectAll.bind(gridView.grid.gridView),
        });
        this.filterService = filterService;
        this.i18n = i18n;

        const inlineChartFactory = loadInlineChartWithWebpack;

        inlineChartFactory({
          serverContext,
          gridView,
          gridDataSource: serverContext.DynamicPermissions.CampaignsCombineGridAndChartOdataCallEnabled && gridDataSource,
          filterService,
          chartContainer: '.campaigns-inline-chart',
          entityType: 'Campaign',
          preexecuteFlag,
          showInContextCard: permissions.dynamic.InContextRecommendationCardPhase1,
          recommendationTypes: compact([
            RECOMMENDATION_TYPES.CREATE_CONVERSION_GOAL,
            RECOMMENDATION_TYPES.SETUP_CONVERSION_TRACKING_OPPORTUNITY,
            RECOMMENDATION_TYPES.FIX_CONVERSION_GOAL_SETTINGS,
            RECOMMENDATION_TYPES.FIX_CONVERSION_TRACKING_OPPORTUNITY,
            RECOMMENDATION_TYPES.BUDGET_OPPORTUNITY,
            RECOMMENDATION_TYPES.ADJUST_SHARED_BUDGET_OPPORTUNITY,
            RECOMMENDATION_TYPES.SSC_2_PMAX_MIGRATION,
            RECOMMENDATION_TYPES.DSA_2_PMAX_MIGRATION,
            RECOMMENDATION_TYPES.SYNDICATION_GAP,
          ]),
          permissions,
          scope,
          newI18n,
          isFluctuationsEnabled: permissions.IsFluctuationEnabled,
          perfMarker,
        }).then((inlineChartView) => {
          this.chartView = inlineChartView;
        });

        this.setState({
          backboneGridView: gridView,
        });

        gridView.grid.gridView.overrideWith = bind(showTopOfGridPanel, this); // eslint-disable-line no-param-reassign
        return {
          default: () => (<StyledGridViewWrapperWithFilterContext gridView={gridView} gridClassName="bingadsgrid small-padding-left" />),
        };
      }).bind(this);
    });

    const toolbarEditor = getToolbarEditor({
      toolbarConfig: { toolbarSelectedConfig, toolbarUnselectedConfig },
      updateToolbar,
    });
    const updateToolbarBinded = bind(toolbarEditor.update, toolbarEditor);
    const FilterBarView = React.lazy(() => { // eslint-disable-line arrow-body-style
      return campaignsGridPromise.then(({ filterService, filterConfig }) => {
        if (this.willUnMount) {
          return {};
        }
        const {
          i18n: legacyI18n,
          ...filterProps
        } = filterConfig;

        const IsRenameHotelToLodgingEnabled = permissions && permissions.dynamic && permissions.dynamic.IsRenameHotelToLodgingEnabled;
        if (IsRenameHotelToLodgingEnabled) { // When enable IsRenameHotelToLodgingEnabled, show text as "Lodging"
          try {
            // eslint-disable-next-line no-param-reassign
            filterService.entitySchema.properties.CampaignType.enumValueMap["Enum.CampaignType'Hotel'"] = 'Lodging';
          } catch (error) { console.log(`Rename hotel to lodging error at campaign page scenario:: ${error}`); }
        }

        return {
          default: () => (
            <FilterViewManagerWithGlobalFilterContext
              filterService={filterService}
              updateToolbar={updateToolbarBinded}
              resetToolbarVisibility={resetToolbarVisibility}
              i18n={newI18n}
              legacyI18n={legacyI18n}
              readOnlyProperties={['SeasonalityAdjustmentId', 'DataExclusionId']}
              beforeApplySavedFilter={filters => onBeforeApplySavedFilter({
               filters, scope, i18n: newI18n, jsdata,
              })}
              predicateValueNameGetter={seasonalityPredicateValueNameGetter}
              permissions={permissions}
              {...filterProps}
            />
          ),
        };
      });
    });

    this.campaignsGrid = (<CampaignsGridView />);
    this.filterViewManager = (<FilterBarView />);
  }

  render() {
    const {
      serverContext: {
        CurrentAccount: { TimeZone },
      },
      panelView,
      renderToolbarContainer,
      showTopGridPanel,
      showInlineChart,
      permissions,
    } = this.props;
    const topGridPanel = showTopGridPanel ? (
      <TopGridPanel
        panelView={panelView}
        customClasses={{
          topGridPanelCustomStyles: this.props.classes.topGridPanelCustomStyles,
        }}
      />) : null;
    const inlineChart = showInlineChart ? this.chartView : null;

    const topOfGridContent = (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        <Fragment>
          <div className={classNames(['campaignContentContainer', this.props.classes.inlineChartContainer])}>
            <section className="campaigns-inline-chart">
              {inlineChart}
            </section>
          </div>
          {topGridPanel}
        </Fragment>
      </React.Suspense>
    );

    const stickyTopOfGridContent = (
      <Fragment>
        {renderToolbarContainer()}
        {this.filterViewManager}
      </Fragment>
    );
    const isSSCUpgradeToPMaxPhase0NotificationEnabled = get(permissions, ['dynamic', 'SSCUpgradeToPMaxNotification'], false);
    const isSSCUpgradeToPMaxPhase12NotificationEnabled = get(permissions, ['dynamic', 'SSCUpgradeToPMaxPhase12Notification'], false);

    const showSscUpgradedPhase12Message = isSSCUpgradeToPMaxPhase12NotificationEnabled && !this.state.phase12SscUpgradedMessageDismissed;
    // Show the Phase 0 message only if the Phase 12 message is not shown
    const showSscUpgradedPhase0Message = isSSCUpgradeToPMaxPhase0NotificationEnabled && !isSSCUpgradeToPMaxPhase12NotificationEnabled && !this.state.phase0SscUpgradedMessageDismissed;
    const sscUpgradedMessage = this.getSscUpgradeMessage(showSscUpgradedPhase12Message);

    return (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        {(showSscUpgradedPhase0Message || showSscUpgradedPhase12Message) && !this.state.isSscUpgradedMessageDismissed && sscUpgradedMessage}
        { this.props.permissions.dynamic && this.props.permissions.dynamic.AutoBiddingCouponCallout &&
          <AutoBiddingCouponCallout />
        }
        { this.props.permissions.dynamic && this.props.permissions.dynamic.FACForShopping &&
          <ShoppingCouponCallout
            activeSuccessCallback={() => window.open(`${location.origin}/campaign/vnext/createcampaign/searchshopping${location.search}`, '_blank')}
            scenarioName={ShoppingFACScenario.FACShoppingInCampaignPage}
          />
        }
        <StyledTablePageContainer
          topOfGridContent={topOfGridContent}
          stickyTopOfGridContent={stickyTopOfGridContent}
          gridView={this.campaignsGrid}
          backboneGridView={this.state.backboneGridView}
          customClasses={{
            tablePageContainer: 'campaigns-page small-padding-left',
            gridContainer: 'campaigns-style-injection',
            gridViewContainer: 'campaigns-table',
          }}
          timeZone={TimeZone}
          scrollToTop={this.props.forceHideToolbar && this.props.showTopGridPanel}
          forceResetScrollFlag={this.props.forceResetScrollFlag}
          CustomDisclaimerBanner={this.props.permissions.IsInStoreVisitGoalEnabled ? DisclaimerBannerWithInstore : null}
        />
        { this.props.permissions.IsNoteEnabled && <NotesPanel
          {...this.props.notesPanelProps}
          openCreateNoteDialog={this.props.noteDialogProps.openCreateNoteDialog}
          i18n={this.props.i18n}
          scope={this.props.scope}
        /> }
        { this.props.permissions.IsNoteEnabled && <NoteDialog
          {...this.props.noteDialogProps}
          i18n={this.props.i18n}
          scope={this.props.scope}
          getSelectedItems={() => this.state.backboneGridView.view.selectedItems()}
        /> }
        <StyledCampaignUpgradeDialog
          {...this.props.campaignUpgradeProps}
          i18n={this.props.i18n}
          scope={this.props.scope}
          refreshGrid={() => {
            import(/* webpackMode: 'eager' */'@bingads-webui-static-legacy/summary/SummaryDataContextManager')
            .then(({ default: dataContextManager }) => {
              dataContextManager.successEmitter.trigger('invalidate', 'Campaign');
              this.state.backboneGridView.view.refresh(true);
            });
          }}
        />
      </React.Suspense>
    );
  }
}

/* eslint-disable react/forbid-prop-types */
Campaigns.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.object.isRequired,
    CurrentCustomer: PropTypes.object.isRequired,
  }).isRequired,
  classes: PropTypes.object.isRequired,
  scope: PropTypes.object.isRequired,
  deps: PropTypes.object,
  preferenceService: PropTypes.object.isRequired,
  permissions: PropTypes.object.isRequired,
  theme: PropTypes.shape({}).isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })).isRequired,
  hotKey: PropTypes.string.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  renderToolbarContainer: PropTypes.func.isRequired,
  crossPageSelectPlugin: PropTypes.shape({
    plugin: PropTypes.func.isRequired,
  }).isRequired,
  toolbarPlugin: PropTypes.shape({
    plugin: PropTypes.func.isRequired,
  }).isRequired,
  panelView: PropTypes.func,
  onGridInjected: PropTypes.func.isRequired,
  showInlineChart: PropTypes.bool.isRequired,
  showTopGridPanel: PropTypes.bool.isRequired,
  toolbarSelectedConfig: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  toolbarUnselectedConfig: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  updateToolbar: PropTypes.func.isRequired,
  resetToolbarVisibility: PropTypes.func.isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
  forceHideToolbar: PropTypes.bool.isRequired,
  forceResetScrollFlag: PropTypes.bool.isRequired,
  notesPanelProps: PropTypes.shape({
    isNotesPanelOpen: PropTypes.bool.isRequired,
    openNotesPanel: PropTypes.func.isRequired,
    closeNotesPanel: PropTypes.func.isRequired,
  }).isRequired,
  noteDialogProps: PropTypes.shape({
    isNoteDialogOpen: PropTypes.bool.isRequired,
    noteDialogMode: PropTypes.string.isRequired,
    openCreateNoteDialog: PropTypes.func.isRequired,
    closeNoteDialog: PropTypes.func.isRequired,
  }).isRequired,
  campaignUpgradeProps: PropTypes.shape({
    toolbarDropdownText: PropTypes.string.isRequired,
    isCampaignUpgradeDialogOpen: PropTypes.bool.isRequired,
    openCampaignUpgradeDialog: PropTypes.func.isRequired,
    closeCampaignUpgradeDialog: PropTypes.func.isRequired,
    selectedCampaigns: PropTypes.arrayOf(PropTypes.shape({
      Id: PropTypes.number.isRequired,
    })).isRequired,
  }).isRequired,
  preexecuteFlag: PropTypes.bool,
  prefetchChunks: PropTypes.func.isRequired,
};

Campaigns.defaultProps = {
  panelView: null,
  preexecuteFlag: false,
  deps: {},
};

export const StyledCampaigns = compose(
  WrappedComponent => withHotKeys(WrappedComponent, ShortcutType.CreateNew),
  withI18nConsumer,
  WrappedComponent => withSiteMap(withDelayedStyles(WrappedComponent, getDefaultStyles, getDelayedCampaignStyles, 'campaigns_grid_loaded')),
  withNoteStateManager,
  withCampaignUpgradeManager,
  withInstrumentation,
  withGlobalFilter,
  withTablePageStateManager({ getToolbarConfig })
)(Campaigns);
